import { Ticker } from "pixi.js";
import type { HeroData } from "../helpers/types";
import { config } from "../settings/config";
import type { HeroesController } from "./HeroesController";

const SNAP_DISTANCE = 0.05;
export const MOVE_LERP_FACTOR = 0.35;
export const TRACK_DISTANCE_SCALE = config.moveSpeed / config.maxDistance;
export const RELEASE_SPEED_MULTIPLIER = 0.35;

type PositionMap = Record<string, number>;
type FinishLock = { nextName?: string; gap: number };

const minimapRefs = {
    container: undefined as HTMLDivElement | undefined,
    racers: new Map<string, HTMLDivElement>(),
};

export const minimapPositionRegistry = {
    registerContainer(element: HTMLDivElement | null) {
        minimapRefs.container = element ?? undefined;
    },

    registerRacer(racerId: string, element: HTMLDivElement | null) {
        if (element) {
            minimapRefs.racers.set(racerId, element);
            return;
        }

        minimapRefs.racers.delete(racerId);
    },
};

export class PositionsController {
    private activeHeroIds = new Set<string>();
    private trackPositions: PositionMap = {};
    private trackTargets: PositionMap = {};
    private minimapPositions: PositionMap = {};
    private minimapTargets: PositionMap = {};
    private trackedHeroId = "";
    private finishLocks = new Map<string, FinishLock>();
    private active = false;
    private released = false;
    private previousDistances = new Map<string, number>();
    private previousDistanceTimestamps = new Map<string, number>();
    private heroVelocities = new Map<string, number>();
    private heroMinimapVelocities = new Map<string, number>();

    constructor(
        private heroes: HeroesController,
    ) {
        Ticker.shared.add(this.tick, this);
    }

    update(activeHeroes: HeroData[], trackedHero?: HeroData) {
        if (this.released) return;

        if (!trackedHero) {
            this.reset();
            return;
        }

        this.active = true;
        this.trackedHeroId = trackedHero.name;
        this.activeHeroIds = new Set(activeHeroes.map((hero) => hero.name));
        this.pruneInactivePositions(this.trackPositions);
        this.pruneInactivePositions(this.minimapPositions);
        this.pruneFinishLocks();
        this.captureVelocities(activeHeroes);

        const heroById = new Map(activeHeroes.map((hero) => [hero.name, hero]));
        this.updateFinishLocks(activeHeroes);

        const trackedDistance = this.getEffectiveDistance(trackedHero.name, heroById);
        const containerWidth = minimapRefs.container?.clientWidth ?? 0;
        const nextTrackTargets: PositionMap = {};
        const nextMinimapTargets: PositionMap = {};

        activeHeroes.forEach((hero) => {
            const distance = this.getEffectiveDistance(hero.name, heroById);
            const trackTarget =
                hero.name === trackedHero.name
                    ? 0
                    : (distance - trackedDistance) * TRACK_DISTANCE_SCALE;
            const progress = Math.max(hero.distance / config.maxDistance, 0);

            nextTrackTargets[hero.name] = trackTarget;
            nextMinimapTargets[hero.name] = progress * containerWidth;

            if (this.trackPositions[hero.name] === undefined) {
                this.trackPositions[hero.name] = 0;
            }

            if (this.minimapPositions[hero.name] === undefined) {
                this.minimapPositions[hero.name] = 0;
            }
        });

        this.trackPositions[trackedHero.name] = 0;
        this.trackTargets = nextTrackTargets;
        this.minimapTargets = nextMinimapTargets;
    }

    reset() {
        this.active = false;
        this.released = false;
        this.activeHeroIds.clear();
        this.trackedHeroId = "";
        this.trackPositions = {};
        this.trackTargets = {};
        this.minimapPositions = {};
        this.minimapTargets = {};
        this.finishLocks.clear();
        this.previousDistances.clear();
        this.previousDistanceTimestamps.clear();
        this.heroVelocities.clear();
        this.heroMinimapVelocities.clear();

        this.heroes.updatePositions({});
        this.updateMinimapPositions({});
    }

    stop() {
        this.active = false;
    }

    release() {
        this.released = true;
    }

    private tick(ticker: Ticker) {
        if (!this.active) return;

        const factor = 1 - Math.pow(1 - MOVE_LERP_FACTOR, ticker.deltaTime);

        if (this.released) {
            let trackChanged = false;
            let minimapChanged = false;

            Object.keys(this.trackPositions).forEach((id) => {
                const velocity = this.heroVelocities.get(id) ?? 0;

                if (velocity > 0) {
                    this.trackPositions[id] += velocity * ticker.deltaMS * RELEASE_SPEED_MULTIPLIER;
                    trackChanged = true;
                }
            });

            Object.keys(this.minimapPositions).forEach((id) => {
                const minimapVelocity = this.heroMinimapVelocities.get(id) ?? 0;

                if (minimapVelocity > 0) {
                    this.minimapPositions[id] += minimapVelocity * ticker.deltaMS;
                    minimapChanged = true;
                }
            });

            if (trackChanged) {
                this.heroes.updatePositions(this.trackPositions);
            }

            if (minimapChanged) {
                this.updateMinimapPositions(this.minimapPositions);
            }

            return;
        }

        const trackChanged = this.interpolatePositions(
            this.trackPositions,
            this.trackTargets,
            factor,
            this.trackedHeroId,
        );
        const minimapChanged = this.interpolatePositions(
            this.minimapPositions,
            this.minimapTargets,
            factor,
        );

        if (trackChanged) {
            this.heroes.updatePositions(this.trackPositions);
        }

        if (minimapChanged) {
            this.updateMinimapPositions(this.minimapPositions);
        }
    }

    private captureVelocities(activeHeroes: HeroData[]) {
        const now = performance.now();
        const containerWidth = minimapRefs.container?.clientWidth ?? 0;
        const minimapScale = containerWidth / config.maxDistance;

        activeHeroes.forEach((hero) => {
            const prevDistance = this.previousDistances.get(hero.name);
            const prevTime = this.previousDistanceTimestamps.get(hero.name);

            if (prevDistance !== undefined && prevTime !== undefined) {
                const dtMs = now - prevTime;
                const distanceDelta = hero.distance - prevDistance;

                if (dtMs > 0 && distanceDelta > 0) {
                    const velocity = (distanceDelta * TRACK_DISTANCE_SCALE) / dtMs;

                    this.heroVelocities.set(hero.name, velocity);

                    if (minimapScale > 0) {
                        const minimapVelocity = (distanceDelta * minimapScale) / dtMs;

                        this.heroMinimapVelocities.set(hero.name, minimapVelocity);
                    }
                }
            }

            this.previousDistances.set(hero.name, hero.distance);
            this.previousDistanceTimestamps.set(hero.name, now);
        });
    }

    private interpolatePositions(
        positions: PositionMap,
        targets: PositionMap,
        factor: number,
        pinnedId?: string,
    ) {
        let changed = false;

        this.activeHeroIds.forEach((id) => {
            const target = targets[id] ?? 0;

            if (id === pinnedId) {
                if (positions[id] !== 0) {
                    positions[id] = 0;
                    changed = true;
                }

                return;
            }

            const next = this.lerp(positions[id] ?? target, target, factor);

            if (positions[id] !== next) {
                positions[id] = next;
                changed = true;
            }
        });

        return changed;
    }

    private pruneInactivePositions(positions: PositionMap) {
        Object.keys(positions).forEach((id) => {
            if (!this.activeHeroIds.has(id)) {
                delete positions[id];
            }
        });
    }

    private pruneFinishLocks() {
        this.finishLocks.forEach((_, id) => {
            if (!this.activeHeroIds.has(id)) {
                this.finishLocks.delete(id);
            }
        });
    }

    private updateFinishLocks(activeHeroes: HeroData[]) {
        const sorted = [...activeHeroes].sort((a, b) => a.position - b.position);

        sorted.forEach((hero, index) => {
            if (this.finishLocks.has(hero.name)) return;
            if (hero.distance < config.maxDistance) return;

            const nextHero = sorted[index + 1];
            const gap = nextHero ? Math.max(hero.distance - nextHero.distance, 0) : 0;

            this.finishLocks.set(hero.name, {
                nextName: nextHero?.name,
                gap,
            });
        });
    }

    private getEffectiveDistance(
        name: string,
        heroById: Map<string, HeroData>,
        visiting = new Set<string>(),
    ): number {
        const hero = heroById.get(name);
        if (!hero) return 0;

        const lock = this.finishLocks.get(name);
        if (!lock) return hero.distance;

        if (!lock.nextName || visiting.has(name)) {
            return config.maxDistance;
        }

        visiting.add(name);
        return this.getEffectiveDistance(lock.nextName, heroById, visiting) + lock.gap;
    }

    private lerp(current: number, target: number, factor: number) {
        const diff = target - current;

        if (Math.abs(diff) < SNAP_DISTANCE) {
            return target;
        }

        return current + diff * factor;
    }

    private updateMinimapPositions(positions: PositionMap) {
        minimapRefs.racers.forEach((element, racerId) => {
            const x = positions[racerId] ?? 0;

            element.style.transform = `translateX(${x}px)`;
        });
    }
}
